/* global COMPONENTS */

import {
  matchesBedroom,
  matchesMoveinDate,
  compareUnits,
  escapeAsHtml,
  renderSkeletonHtml,
  renderUnitHtml
} from './_render.js'

// Spaces Light v3: a lightweight, theme-side widget that sources unit data from the
// public, unauthenticated SPACES v3 REST endpoints (no React/spaces-app dependency).
// There is no server-rendered first paint (see _standard-v3.twig/php/_base.php) -- this
// module fetches the FULL set of available units for the asset ONCE on load
// (fetchAllUnits(), called via loadUnits() at the end of init()), auto-paging internally
// (the REST endpoint caps per_page at 100) up to a safety limit. Every subsequent
// bedroom/move-in-date/sort/pagination interaction then runs entirely client-side against
// that already-fetched dataset (see renderCurrentState()) -- no repeat network calls.
// This keeps all live inventory data (units, matching count, pagination) out of the
// cacheable HTML entirely, so a CDN/edge cache can never serve stale availability/pricing,
// while also avoiding a fresh REST round trip for every filter/sort/page click (matching
// the fetch-once/client-side-filter architecture used by the studio/spaces-light block).
// Uses native window.fetch() (not jQuery $.ajax()) for the REST call, matching that
// reference implementation's HTTP client; jQuery is still used for DOM/event wiring.
COMPONENTS.ComponentSpaceslightV3 = (function ($) {
  // Maximum units pages fetched per asset (at the REST endpoint's 100-per-page cap) before
  // giving up on fetching more -- i.e. a hard safety cap of 500 available units. No real
  // property is expected to approach this; if one ever does, only the first 500 (by the
  // server's default ordering) are shown client-side and a console warning is logged.
  var FETCH_PER_PAGE = 100
  var SAFETY_MAX_PAGES = 5

  var LOADING_CLASS = 'is-loading'
  var READY_CLASS = 'is-ready'
  var EMPTY_CLASS = 'is-empty'
  var ERROR_CLASS = 'is-error'

  function ComponentSpaceslightV3 () {
    this.$instances = $('[data-spaceslight-v3]')
  }

  /**
   * Toggle the widget root's loading/ready/empty/error state classes and update the
   * dedicated status-text node (announced to screen readers via aria-live), matching
   * the studio/spaces-light reference block's setBlockState() pattern.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @param {string} nextState One of LOADING_CLASS/READY_CLASS/EMPTY_CLASS/ERROR_CLASS.
   * @param {string} [message] Status text to display/announce (blank when ready).
   */
  function setBlockState (ctx, nextState, message) {
    ctx.$instance.removeClass([LOADING_CLASS, READY_CLASS, EMPTY_CLASS, ERROR_CLASS].join(' '))
    ctx.$instance.addClass(nextState)
    ctx.$unitsContainer.attr('aria-busy', nextState === LOADING_CLASS ? 'true' : 'false')
    ctx.$state.text(message || '')
  }

  /**
   * Build the units REST URL for a single page of the (locked-class-filtered, but
   * otherwise unfiltered/unsorted) full dataset fetch -- bedroom/move-in-date/sort
   * are applied client-side afterward, so they're intentionally NOT sent here.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @param {number} page The page number to request.
   * @return {string} The request URL.
   */
  function buildUnitsUrl (ctx, page) {
    const config = ctx.config
    const params = new URLSearchParams()
    params.set('post_id', config.assetPostId)
    params.set('page', page)
    params.set('per_page', FETCH_PER_PAGE)

    if (config.lockedFilterClasses) {
      params.set('css_class_groups', config.lockedFilterClasses)
    }

    return config.restUrl + 'assets/' + encodeURIComponent(config.engrainAssetId) + '/units?' + params.toString()
  }

  /**
   * Fetch a single page of the units REST endpoint using native fetch().
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @param {number} page The page number to request.
   * @return {Promise<Object>} Resolves with the parsed JSON response body.
   */
  async function fetchUnitsPage (ctx, page) {
    const response = await window.fetch(buildUnitsUrl(ctx, page), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })

    if (!response.ok) {
      throw new Error('Request failed with status ' + response.status)
    }

    return response.json()
  }

  /**
   * Fetch every available unit for this asset, auto-paging past the REST endpoint's
   * 100-per-page cap (up to SAFETY_MAX_PAGES) so the rest of the module can treat the
   * result as a single, complete in-memory dataset.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @return {Promise<Array>} Resolves with the full (capped) units array.
   */
  async function fetchAllUnits (ctx) {
    const firstPage = await fetchUnitsPage(ctx, 1)
    let units = (firstPage && firstPage.data) || []
    const totalPages = (firstPage && firstPage.pagination && firstPage.pagination.total_pages) || 1
    const pagesToFetch = Math.min(totalPages, SAFETY_MAX_PAGES)

    if (totalPages > SAFETY_MAX_PAGES) {
      // eslint-disable-next-line no-console
      console.warn('Spaces Light v3: asset ' + ctx.config.engrainAssetId + ' has more than ' + (SAFETY_MAX_PAGES * FETCH_PER_PAGE) + ' available units; only the first ' + (SAFETY_MAX_PAGES * FETCH_PER_PAGE) + ' are shown.')
    }

    for (let page = 2; page <= pagesToFetch; page++) {
      const nextPage = await fetchUnitsPage(ctx, page)
      units = units.concat((nextPage && nextPage.data) || [])
    }

    return units
  }

  /**
   * Render a units response shape (data + pagination) into the DOM -- shared by the
   * client-side pagination pipeline (renderCurrentState()) below.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @param {Object} response The response body: { data, pagination }.
   */
  function renderResponse (ctx, response) {
    const config = ctx.config
    const state = ctx.state
    const units = (response && response.data) || []
    const pagination = (response && response.pagination) || { current_page: 1, total_pages: 1, total_items: 0 }

    if (!units.length) {
      ctx.$unitsContainer.html('')
      setBlockState(ctx, EMPTY_CLASS, config.noResultsLabel || 'No available units match the selected filters.')
    } else {
      ctx.$unitsContainer.html(units.map(function (unit) {
        return renderUnitHtml(unit, config)
      }).join(''))
      setBlockState(ctx, READY_CLASS, '')
    }

    state.page = pagination.current_page
    state.totalPages = pagination.total_pages

    if (ctx.$matchingCount.length) {
      const matchingCountHtml = '<strong data-spaceslight-matching-count-value>' + escapeAsHtml(pagination.total_items) + '</strong> Matching Unit' + (pagination.total_items === 1 ? '' : 's')
      ctx.$matchingCount.html(/* safe */ matchingCountHtml)
    }

    ctx.$pagination.attr('data-current-page', state.page)
    ctx.$pagination.attr('data-total-pages', state.totalPages)
    ctx.$pagination.toggleClass('is-hidden', state.totalPages <= 1)
    ctx.$pagination.find('[data-spaceslight-page-prev]').prop('disabled', state.page <= 1)
    ctx.$pagination.find('[data-spaceslight-page-next]').prop('disabled', state.page >= state.totalPages)
    ctx.$pagination.find('.spaceslight-v3-pagination-status').text('Page ' + state.page + ' of ' + state.totalPages)
  }

  /**
   * Apply the current bedroom/move-in-date filters and sort to the already-fetched
   * `state.allUnits` dataset, slice out the current page, and render -- entirely
   * client-side, no network call. Called for every filter/sort/pagination change.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   */
  function renderCurrentState (ctx) {
    const config = ctx.config
    const state = ctx.state
    const matched = state.allUnits.filter(function (unit) {
      return matchesBedroom(unit, state.beds) && matchesMoveinDate(unit, state.availableOn)
    })

    matched.sort(function (a, b) {
      return compareUnits(a, b, state.sort)
    })

    const perPage = config.perPage || 8
    const totalItems = matched.length
    const totalPages = perPage > 0 ? Math.max(1, Math.ceil(totalItems / perPage)) : 1
    state.page = Math.min(Math.max(state.page, 1), totalPages)
    const pageItems = matched.slice((state.page - 1) * perPage, state.page * perPage)

    renderResponse(ctx, {
      data: pageItems,
      pagination: {
        current_page: state.page,
        total_pages: totalPages,
        total_items: totalItems
      }
    })
  }

  /**
   * Fetch the full units dataset once and render the first page -- the only network
   * call this widget makes (besides the one-time Sightmap calculator iframe, if used).
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   */
  function loadUnits (ctx) {
    setBlockState(ctx, LOADING_CLASS, 'Loading current pricing and availability\u2026')
    ctx.$unitsContainer.html(renderSkeletonHtml(ctx.config.perPage))

    fetchAllUnits(ctx).then(function (units) {
      ctx.state.allUnits = units
      ctx.state.page = 1
      renderCurrentState(ctx)
    }).catch(function () {
      ctx.$unitsContainer.html('')
      setBlockState(ctx, ERROR_CLASS, 'We were unable to load current pricing and availability. Please refresh and try again.')
    })
  }

  /**
   * Apply the selected move-in date to state and re-render client-side.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   * @param {string} value The picked date, already in YYYY-MM-DD form.
   */
  function applyMoveinDate (ctx, value) {
    ctx.state.availableOn = value || ''
    ctx.state.page = 1
    renderCurrentState(ctx)
  }

  /**
   * Wire up the move-in-date filter: a themed Pikaday date picker when the library
   * loaded successfully, falling back to the plain text field (manual YYYY-MM-DD
   * entry) otherwise so the filter stays usable either way.
   *
   * Extracted out of initInstance() to keep that function's line count within the
   * configured threshold.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   */
  function setupMoveinDatePicker (ctx) {
    const $moveinFilter = ctx.$moveinFilter

    if ($moveinFilter.length && typeof window.Pikaday !== 'undefined') {
      // Same date picker library used by the Hero component's move-in date fields
      // (component-hero/templates/partials/_market_search.twig / _quick_search.twig).
      // The custom toString/parse pair keeps the field's value as YYYY-MM-DD (matching
      // the REST `available_on` param) regardless of the display `format`.
      const minDate = new Date()
      const maxDate = new Date()
      maxDate.setDate(maxDate.getDate() + (ctx.config.maxDaysOut || 90))

      // eslint-disable-next-line no-new
      ctx.pikadayInstance = new window.Pikaday({
        field: $moveinFilter.get(0),
        // Render the calendar inside the filter field's own wrapper instead of
        // Pikaday's default (appending to <body>) -- this is required so `.pika-single`
        // is a real descendant of `.spaceslight-v3`/`.spaces-root` and can therefore
        // inherit the live, per-asset `--spaces-color-*` custom properties used to
        // theme it (see `.pika-single` rules in _base.scss). Scoped to just this field;
        // the Hero component's separate Pikaday instances are unaffected.
        container: $moveinFilter.closest('[data-spaces-control="picker-container"]').get(0),
        format: 'm-d-Y',
        minDate: minDate,
        maxDate: maxDate,
        defaultDate: minDate,
        toString: function (date) {
          const day = date.getDate().toString().padStart(2, '0')
          const month = (date.getMonth() + 1).toString().padStart(2, '0')
          const year = date.getFullYear()
          return year + '-' + month + '-' + day
        },
        parse: function (dateString) {
          const parts = dateString.split('-')
          const year = parseInt(parts[0], 10)
          const month = parseInt(parts[1], 10) - 1
          const day = parseInt(parts[2], 10)
          return new Date(year, month, day)
        },
        onSelect: function () {
          applyMoveinDate(ctx, $moveinFilter.val())
        }
      })

      $moveinFilter.prop('readonly', true)
    } else {
      // Fallback if Pikaday failed to load: keep the plain text field usable via
      // manual YYYY-MM-DD entry rather than losing the filter entirely.
      $moveinFilter.on('change', function () {
        applyMoveinDate(ctx, $(this).val())
      })
    }
  }

  /**
   * Bind the bedroom/sort/pagination/reset filter controls' change/click handlers,
   * each of which mutates `ctx.state` and re-renders entirely client-side.
   *
   * Extracted out of initInstance() to keep that function's line count within the
   * configured threshold.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   */
  function bindFilterEvents (ctx) {
    const state = ctx.state

    ctx.$bedroomFilter.on('change', function () {
      state.beds = $(this).val() || ''
      state.page = 1
      renderCurrentState(ctx)
    })

    ctx.$sortFilter.on('change', function () {
      state.sort = $(this).val() || ctx.config.defaultSort
      state.page = 1
      renderCurrentState(ctx)
    })

    ctx.$pagination.on('click', '[data-spaceslight-page-prev]', function () {
      if (state.page > 1) {
        state.page -= 1
        renderCurrentState(ctx)
      }
    })

    ctx.$pagination.on('click', '[data-spaceslight-page-next]', function () {
      if (state.page < state.totalPages) {
        state.page += 1
        renderCurrentState(ctx)
      }
    })

    ctx.$resetFilters.on('click', function () {
      state.beds = ''
      state.availableOn = ''
      state.sort = ctx.config.defaultSort || 'pr_asc'
      state.page = 1

      ctx.$bedroomFilter.val('')
      if (ctx.$bedroomFilter.next('.nice-select').length) {
        ctx.$bedroomFilter.niceSelect('update')
      }

      ctx.$sortFilter.val(state.sort)
      if (ctx.$sortFilter.next('.nice-select').length) {
        ctx.$sortFilter.niceSelect('update')
      }

      ctx.$moveinFilter.val('')
      if (ctx.pikadayInstance) {
        ctx.pikadayInstance.setDate(null, true)
      }

      renderCurrentState(ctx)
    })
  }

  /**
   * Wire up the Sightmap expense calculator modal, restoring the v2 shortcode's
   * calculator-trigger behavior (SPACES.CalculatorTrigger in spaces_scripts.js),
   * reimplemented standalone since this widget has no spaces-app/SPACES.spacesModal
   * dependency. One modal/iframe per widget instance, reused for every unit row
   * (server- or client-rendered -- delegated so it also matches AJAX-rendered rows).
   * A no-op when the asset/component doesn't have the calculator enabled.
   *
   * Extracted out of initInstance() to keep that function's line count within the
   * configured threshold.
   *
   * @param {Object} ctx A single widget instance's context, as built by buildInstanceCtx().
   */
  function setupCalculatorModal (ctx) {
    const config = ctx.config
    const $calculatorModal = ctx.$calculatorModal
    const $calculatorFrame = ctx.$calculatorFrame

    if (!config.enableCalculator || !$calculatorModal.length || !config.sightmapEmbedUrl) {
      return
    }

    /**
     * Close the calculator modal and clear the iframe src (stops any
     * audio/playback and avoids showing stale data on next open).
     */
    const closeCalculatorModal = function () {
      $calculatorModal.attr('aria-hidden', 'true').removeClass('is-open')
      $calculatorFrame.attr('src', '')
      $('body').removeClass('spaceslight-v3-calculator-open')
    }

    ctx.$instance.on('click', '[data-spaceslight-calculator-trigger]', function (e) {
      e.preventDefault()
      const $trigger = $(this)

      if ($trigger.hasClass('spaces-disabled')) {
        return
      }

      $trigger.addClass('spaces-disabled')

      const unitNumber = $trigger.attr('data-spaces-unit-number') || ''
      const leaseTerm = $trigger.attr('data-spaces-calculator-lease-term') || ''
      const moveInDate = $trigger.attr('data-spaces-calculator-date') || ''
      const params = new URLSearchParams()
      params.set('unit_number', unitNumber)
      params.set('lease_term', leaseTerm)
      params.set('move_in_date', moveInDate)
      params.set('mode', 'expense_calculator')

      $calculatorFrame.attr('src', config.sightmapEmbedUrl + '?' + params.toString())
      $calculatorModal.attr('aria-hidden', 'false').addClass('is-open')
      $('body').addClass('spaceslight-v3-calculator-open')
      $trigger.removeClass('spaces-disabled')
    })

    $calculatorModal.on('click', '[data-spaceslight-calculator-close]', function (e) {
      e.preventDefault()
      closeCalculatorModal()
    })

    $(document).on('keydown', function (e) {
      if (e.key === 'Escape' && $calculatorModal.hasClass('is-open')) {
        closeCalculatorModal()
      }
    })
  }

  /**
   * Collect a single widget instance's DOM references and initial filter/pagination
   * state into one shared context object, passed to every helper function above
   * instead of relying on closure variables.
   *
   * Extracted out of initInstance() to keep that function's line count within the
   * configured threshold.
   *
   * @param {jQuery} $instance The widget instance's root element.
   * @param {Object} config The instance's parsed `data-spaceslight-config` data.
   * @return {Object} The instance context.
   */
  function buildInstanceCtx ($instance, config) {
    const $calculatorModal = $instance.find('[data-spaceslight-calculator-modal]')

    return {
      $instance: $instance,
      config: config,
      $unitsContainer: $instance.find('[data-spaceslight-units]'),
      $pagination: $instance.find('[data-spaceslight-pagination]'),
      $bedroomFilter: $instance.find('[data-spaceslight-filter="beds"]'),
      $moveinFilter: $instance.find('[data-spaceslight-filter="available_on"]'),
      $sortFilter: $instance.find('[data-spaceslight-filter="sort"]'),
      $matchingCount: $instance.find('[data-spaceslight-matching-count]'),
      $resetFilters: $instance.find('[data-spaceslight-reset]'),
      $state: $instance.find('[data-spaceslight-state]'),
      $calculatorModal: $calculatorModal,
      $calculatorFrame: $calculatorModal.find('[data-spaceslight-calculator-frame]'),
      pikadayInstance: null,
      state: {
        page: 1,
        totalPages: 1,
        beds: '',
        availableOn: '',
        sort: config.defaultSort || 'pr_asc',
        allUnits: []
      }
    }
  }

  /**
   * Initialize a single `[data-spaceslight-v3]` widget instance: parse its bootstrap
   * config, build its shared context, fetch the full units dataset once (the only
   * network call this widget makes besides the Sightmap calculator iframe, if used),
   * and wire up its filter/pagination/calculator controls.
   *
   * Extracted out of ComponentSpaceslightV3.prototype.init() to keep that function's
   * line count within the configured threshold.
   *
   * @param {jQuery} $instance The widget instance's root element.
   */
  function initInstance ($instance) {
    let config

    try {
      config = JSON.parse($instance.attr('data-spaceslight-config') || '{}')
    } catch (e) {
      return
    }

    if (!config.restUrl || !config.engrainAssetId) {
      return
    }

    const ctx = buildInstanceCtx($instance, config)

    // Fetch the full units dataset immediately on load -- there is no server-rendered
    // first paint (see _standard-v3.twig/php/_base.php), so this is what actually
    // populates the units/matching-count/pagination the very first time the widget is
    // seen. This keeps the cacheable HTML free of any live inventory data. Every
    // subsequent filter/sort/pagination interaction re-renders from this same
    // already-fetched dataset (renderCurrentState()) instead of re-fetching.
    loadUnits(ctx)

    bindFilterEvents(ctx)
    setupMoveinDatePicker(ctx)
    setupCalculatorModal(ctx)
  }

  ComponentSpaceslightV3.prototype.init = function () {
    this.$instances.each(function () {
      initInstance($(this))
    })
  }

  return ComponentSpaceslightV3
})(jQuery)

document.addEventListener('components_init', function () {
  // Hook into the main components init
  COMPONENTS.ComponentSpaceslightV3 = new COMPONENTS.ComponentSpaceslightV3()
  COMPONENTS.ComponentSpaceslightV3.init()
})
